import { z } from "zod";
import { IsoDateTime, Uuid } from "./common.js";

// ---- Attached documents of a study (pilot request 2026-11-03 11:15, migration 0032, ADR 0030) --
//
// "Me los entregan en PDF": a biopsy is processed by an outside pathology laboratory that returns
// its own signed report. In Captura the chemist attaches that PDF (or a photo of it) to the study
// instead of retyping it. The file is uploaded first (`results.attachment.upload`, a staged
// artifact of the study), and becomes part of the result when the capture or the correction is
// saved with it in `attachments` — the same draft → save → validate flow as every other value, so
// the validation hash covers it and a validated attachment changes only through an amendment.
// The report appends the attached pages, unchanged, after the study's own page.

/** Caps shared by the web app and the API. */
export const ATTACHMENT_LIMITS = {
  /** One file, as uploaded. The web app re-encodes large photos before sending them. */
  maxBytes: 15 * 1024 * 1024,
  /** Attached files per study. */
  maxFiles: 10,
  /** Pages of one attached PDF. */
  maxPages: 50,
  /** The name shown in Captura (the original file name, trimmed). */
  maxNameLength: 120,
  /** «Laboratorio que realizó el estudio». */
  maxLabNameLength: 120,
  /** Photos wider or taller than this are scaled down in the browser before upload. */
  photoMaxSidePx: 2400,
} as const;

export const AttachmentContentType = z.enum(["application/pdf", "image/jpeg", "image/png"]);
export type AttachmentContentType = z.infer<typeof AttachmentContentType>;

const Base64 = z.string().regex(/^[A-Za-z0-9+/]+={0,2}$/, "Archivo inválido.");

/**
 * `results.attachment.upload` (Químico or above): stores the file as a staged artifact of the
 * study and answers its metadata. It is not part of the result until a `results.capture` or
 * `results.amend` names it in `attachments`. The server sniffs the bytes: the declared type must
 * match, a PDF must open without a password and copy cleanly, an image must decode.
 */
export const AttachmentUploadPayload = z
  .object({
    order_item_id: Uuid,
    file_name: z.string().trim().min(1).max(255),
    content_type: AttachmentContentType,
    base64: Base64.min(8).max(Math.ceil(ATTACHMENT_LIMITS.maxBytes / 3) * 4),
  })
  .strict();
export type AttachmentUploadPayload = z.infer<typeof AttachmentUploadPayload>;

export const AttachmentInfo = z.object({
  id: Uuid,
  order_item_id: Uuid,
  file_name: z.string(),
  content_type: AttachmentContentType,
  byte_size: z.number().int(),
  /** Pages it adds to the report: the PDF's pages, or 1 for a photo. */
  page_count: z.number().int(),
  sha256_hex: z.string().length(64),
  uploaded_at: IsoDateTime,
});
export type AttachmentInfo = z.infer<typeof AttachmentInfo>;

/** The study's attachments as the capture or amendment sets them: ids in print order; [] removes them all. */
export const AttachmentIdsInput = z.array(Uuid).max(ATTACHMENT_LIMITS.maxFiles, `Se pueden adjuntar hasta ${ATTACHMENT_LIMITS.maxFiles} archivos por estudio.`);

/** «Laboratorio que realizó el estudio» (NOM-007 3.3 / 6.2, ADR 0030); "" clears it. */
export const PerformedByLabInput = z.string().trim().max(ATTACHMENT_LIMITS.maxLabNameLength);

/** "Informe anexo (3 páginas)". */
export function annexPagesText(pages: number): string {
  return pages === 1 ? "1 página" : `${pages} páginas`;
}
